import { AutoAwesome, Block, Subject, VpnKey } from '@mui/icons-material'
import { T, useTranslate } from '@tolgee/react'
import axios from 'axios'
import classNames from 'classnames'
import { ContextMenu } from 'components/context-menu'
import { ContextModal } from 'components/document-context/ContextModal'
import { useAccess } from 'components/hooks/access'
import { ViewContext } from 'components/lib'
import { errorToast, queryToObj, successToast } from 'helpers'
import { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { selectIsReady } from 'store/generations'
import UpgradePlan from 'toolComponents/writer/UpgradePlan'
import { BasicTooltip, ButtonGroup, Microbutton, ReactSelect, SelectionPopover, Slider } from 'ui'
import { ContextField } from '../../../components/document-context/ContextField'
import { modes, styles } from '../models/imageOptions'
import { resolutions } from '../models/imageSizes'
import { Footer } from './Footer'
import { Header } from './Header'

const ImageForm = (props) => {
  const { state, dispatch, setModalData } = props
  const context = useContext(ViewContext)
  const hasAccess = useAccess()
  const allowStandard = hasAccess('Standard')
  const { t } = useTranslate()
  const location = useLocation()

  const enhanceRef = useRef()
  const descriptionRef = useRef()

  const isReady = useSelector(selectIsReady)

  const [templates, setTemplates] = useState({ user: [], predefined: [] })
  const [lastUsedTemplate, setLastUsedTamplate] = useState()

  // Load form data from query params
  useEffect(() => {
    const data = queryToObj(location.search)

    dispatch({
      type: 'APPLY_TEMPLATE',
      payload: data,
    })
  }, [location])

  const getAvailableProportions = useCallback(() => {
    const styleData = styles.find((s) => s.value === state.style)
    const res = resolutions[styleData?.modelType ?? 'sdxl']

    return res.flat()
  }, [state.style])

  const fetchTemplates = useCallback(async () => {
    try {
      const url = '/api/image/create/templates'
      const res = await axios.get(url)

      // Predefined templates should be added in the api and here
      setTemplates({ user: res.data.data, predefined: [] })
    } catch (err) {
      context.handleError(err)
    }
  }, [])

  useEffect(() => {
    fetchTemplates()
  }, [fetchTemplates])

  // Update image proportions when changing style (different options available for different styles)
  useLayoutEffect(() => {
    const availableProportions = getAvailableProportions()
    if (
      !availableProportions ||
      availableProportions.find((item) => item.value === state.chosenProportions)
    )
      return

    const options = [...resolutions.sd15, ...resolutions.sdxl]
    const previousProportions = options.find((item) => item.value === state.chosenProportions)
    if (!previousProportions) return

    const newProportions = availableProportions.find(
      (item) => item.proportions === previousProportions.proportions
    )

    dispatch({
      type: 'SET_PROPORTIONS',
      payload: newProportions.value,
    })
  }, [state.style, getAvailableProportions, state.chosenProportions])

  function loadTemplate(id) {
    if (!allowStandard) return showUpgradePlanModal()

    const templateData = [...templates.user, ...templates.predefined].find((item) => item.id === id)
    if (!templateData) return

    dispatch({
      type: 'APPLY_TEMPLATE',
      payload: templateData,
    })
    setLastUsedTamplate(templateData)
    templateData.stories?.forEach((story) => {
      const storyData = props.documentContext.stories.data.find((x) => x.id === story)
      props.documentContext.addDocument({
        label: storyData.label,
        type: 'story',
        content: storyData.content,
        id: storyData.id,
      })
    })
    successToast(t('eleo-template-applied', 'Template applied'))
  }

  function handleSubmit(e) {
    if (e) e.preventDefault()

    if (!state.prompt) {
      errorToast(<T keyName='eleo-error-prompt-required'>The prompt field is required</T>)
      return false
    }

    // props.setIsImageInQueue(true)
    props.setIsLoading(true)
    props.requestGeneration(state)

    return true
  }

  function showUpgradePlanModal() {
    context.modal.show({
      children: <UpgradePlan />,
      modalCardClassNames: '!bg-transparent !px-[0px] !py-[0px]',
    })
  }

  async function handleSaveTemplate(name) {
    try {
      const url = '/api/image/save-template'
      const data = {
        image_templates: {
          templateName: name,
          description: state.prompt,
          keywords: state.keywords,
          avoid: state.negativePrompt,
          style: state.style,
          samples: state.samples,
          proportions: state.chosenProportions,
          // size: state.imageSize.value,
          quality: state.quality,
          creativity: state.creativity,
          stories: props.documentContext.docContext.map((item) => item.id),
        },
        tool: 'create',
      }

      await axios.post(url, data)
      successToast(t('eleo-save-template-success', 'Template saved successfully!'))
      fetchTemplates()
    } catch (err) {
      context.handleError(err)
    }
  }

  async function handleDeleteTemplate(templateId) {
    const data = { templateId: templateId }
    try {
      await axios.post('/api/image/delete-template', data)
      successToast(t('eleo-delete-template-success', 'Template deleted successfully!'))
      fetchTemplates()
    } catch (err) {
      context.handleError(err)
    }
  }

  async function handleUpdateTemplate(templateId, newContent) {
    const data = {
      image_templates: {
        description: state.prompt,
        keywords: state.keywords,
        avoid: state.negativePrompt,
        style: state.style,
        samples: state.samples,
        proportions: state.chosenProportions,
        quality: state.quality,
        creativity: state.creativity,
        stories: props.documentContext.docContext.map((item) => item.id),
        ...newContent,
      },
      tool: 'create',
      templateId: templateId,
    }

    try {
      await axios.post('/api/image/update-template', data)
      successToast(t('eleo-update-template-success', 'Template updated successfully!'))
      fetchTemplates()
    } catch (err) {
      context.handleError(err)
    }
  }

  return (
    <>
      <div className='flex h-full w-full flex-col'>
        <Header
          title={t('eleo-tool-create-image-header', 'Create Image')}
          templates={templates}
          setTemplate={loadTemplate}
          handleSaveTemplate={handleSaveTemplate}
          handleDeleteTemplate={handleDeleteTemplate}
          handleUpdateTemplate={handleUpdateTemplate}
        />

        {/* Form */}
        <div className='flex flex-1 flex-col gap-y-[14px] overflow-auto px-[14px] py-[16px] text-[13px] lg:gap-y-4 lg:px-5 lg:py-[22px]'>
          {/* <div className='flex flex-1 flex-col gap-y-4 px-5 py-[22px]'> */}
          {/* Row 1 */}
          <div className='flex flex-wrap gap-5'>
            {/* Style */}
            <div className='min-w-[230px] flex-1 space-y-[5px]'>
              <div className='body-small text-brand-gray-dark pl-[2px]'>
                {t('eleo-write-text-style', 'style')}
              </div>
              <div
                id='style'
                className='h-[36px]'
                data-tooltip-id='images-imageCreator-style'
                data-tooltip-keyname='eleo-tooltip'
              >
                <ReactSelect
                  disabled={!state.allowStyles}
                  className={classNames(!state.allowStyles && 'opacity-50')}
                  options={styles}
                  placeHolder={t('eleo-select-drop-down-placeholder', 'Select')}
                  value={styles.find((style) => style.value === state.style) || null}
                  setSelectedValue={(val) =>
                    dispatch({
                      type: 'SET_STYLE',
                      payload: val.value,
                    })
                  }
                  menuPlacement='bottom'
                  portalToBody
                  withImages
                  menuWidth='auto'
                  onClick={
                    !props.isLayoutLarge
                      ? () => {
                          setModalData({
                            isVisible: true,
                            title: (
                              <T
                                keyName='eleo-styles-modal-title'
                                defaultValue='Select image style'
                              />
                            ),
                            subtitle: (
                              <T
                                keyName='eleo-styles-modal-subtitle'
                                defaultValue='Each style is just a starting point. You can achieve very different effects using the description and avoid fields in the next step.'
                              />
                            ),
                            type: 'styles',
                            context: {},
                          })
                        }
                      : undefined
                  }
                />
              </div>
            </div>
            {/* Context */}
            <div className='min-w-[230px] flex-1'>
              <ContextField documentContext={props.documentContext} onlyStory />
            </div>
          </div>

          {/* Row 2 - Description */}
          <div className='space-y-[5px]'>
            <div className='body-small text-brand-gray-dark pl-[2px]'>
              {t('eleo-tool-create-image-content', 'Image content')}
            </div>
            <div className='border-brand-gray-light rounded-md border bg-white'>
              {/* Positive */}
              <div className='relative'>
                <SelectionPopover.Root>
                  <SelectionPopover.Trigger className='relative'>
                    {/* Hidden element for enhance popover menu */}
                    <pre
                      ref={enhanceRef}
                      className='pointer-events-none absolute top-0 w-full text-wrap px-[14px] py-[10px] pb-8 indent-[22px] opacity-0'
                      style={{ fontFamily: 'Rubik, sans-serif' }}
                    >
                      {state.prompt}
                    </pre>
                    <textarea
                      ref={descriptionRef}
                      spellCheck={false}
                      className='min-h-44 w-full resize-none rounded-t-md px-[14px] py-[10px] pb-8 indent-[22px]'
                      placeholder={t(
                        'eleo-tool-create-image-description-placeholder',
                        'What image do you want to generate?'
                      )}
                      style={{ fieldSizing: 'content' }}
                      value={state.prompt}
                      onChange={(e) =>
                        dispatch({
                          type: 'SET_PROMPT',
                          payload: e.target.value,
                        })
                      }
                    />
                  </SelectionPopover.Trigger>
                  <SelectionPopover.Portal>
                    <SelectionPopover.Content
                      side='bottom'
                      align='start'
                      collisionPadding={{ bottom: 50 }}
                      className='z-[9999]'
                    >
                      <ContextMenu
                        noHeader
                        type='image'
                        selectedText={state.prompt}
                        handleChangeSelectedText={(newText) => {
                          enhanceRef.current?.dispatchEvent(
                            new KeyboardEvent('keydown', {
                              key: 'Escape',
                              code: 'Escape',
                              bubbles: true,
                              cancelable: true,
                            })
                          )

                          dispatch({
                            type: 'SET_PROMPT',
                            payload: newText,
                          })
                        }}
                        deleteSelection={() => {
                          enhanceRef.current?.dispatchEvent(
                            new KeyboardEvent('keydown', {
                              key: 'Escape',
                              code: 'Escape',
                              bubbles: true,
                              cancelable: true,
                            })
                          )

                          dispatch({
                            type: 'SET_PROMPT',
                            payload: '',
                          })
                        }}
                        getTextContext={() => state.prompt}
                        tools={['rephrase']}
                        forceNotSynonyms
                        regenerateUrl='/api/ai/enhance_prompt'
                      />
                    </SelectionPopover.Content>
                  </SelectionPopover.Portal>
                </SelectionPopover.Root>
                <BasicTooltip
                  content={t(
                    'eleo-images-description-tooltip-content',
                    "What should be on the image? What's the mood? What is the perspective? What colors do you want to use?"
                  )}
                  title={t('eleo-images-description-tooltip-title', 'Description')}
                  id='image-description-tooltip'
                />

                <div
                  className='text-brand-form-accent absolute left-1 top-1 flex cursor-pointer p-[6px] text-[20px]'
                  data-tooltip-id='image-description-tooltip'
                >
                  <Subject fontSize='inherit' />
                </div>

                {/* Enhance */}
                <Microbutton
                  tooltipId={`eleo-tooltip`}
                  tooltipKeyname={`eleo-tooltip-enchance-prompt`}
                  // title={t('eleo-image-enhance-button', 'Enhance prompt')}
                  iconPlacement='left'
                  variant='transparent-light'
                  icon={<AutoAwesome fontSize='18' />}
                  text={t('eleo-enhance', 'Enhance')}
                  className={classNames('!absolute bottom-2 right-2 h-[28px]')}
                  onClick={() => {
                    if (enhanceRef.current) {
                      const selection = window.getSelection()
                      const range = document.createRange()
                      selection.removeAllRanges()
                      range.selectNodeContents(enhanceRef.current)
                      selection.addRange(range)

                      const customEvent = new PointerEvent('pointerup', {
                        bubbles: true,
                        cancelable: true,
                        pointerType: 'mouse',
                        clientX: 0,
                        clientY: 0,
                      })

                      enhanceRef.current.dispatchEvent(customEvent)

                      const textareaRange = document.createRange()
                      textareaRange.selectNodeContents(descriptionRef.current)
                      setTimeout(() => {
                        selection.removeAllRanges()
                        selection.addRange(textareaRange)
                      }, 50)
                    }
                  }}
                />
              </div>

              {/* Negative */}
              <div className='relative'>
                <textarea
                  spellCheck={false}
                  className='max-h-32 w-full resize-none border-t px-[14px] pb-1 pt-[9px] indent-[22px]'
                  placeholder={t(
                    'eleo-tool-create-image-avoid-placeholder',
                    "What don't you want to see in you image?"
                  )}
                  style={{ fieldSizing: 'content' }}
                  value={state.negativePrompt}
                  onChange={(e) =>
                    dispatch({
                      type: 'SET_NEGATIVE_PROMPT',
                      payload: e.target.value,
                    })
                  }
                />
                <BasicTooltip
                  content={t(
                    'eleo-images-negative-tooltip-content',
                    "What you don't want to see on the image?"
                  )}
                  title={t('eleo-images-negative-tooltip-title', 'Avoid')}
                  id='image-negative-tooltip'
                />

                <div
                  className='text-brand-form-accent absolute left-1 top-1 flex cursor-pointer p-[6px] text-[20px]'
                  data-tooltip-id='image-negative-tooltip'
                >
                  <Block fontSize='inherit' />
                </div>
              </div>

              {/* Keywords */}
              <div className='relative'>
                <textarea
                  spellCheck={false}
                  className='max-h-32 w-full resize-none border-t px-[14px] pb-1 pt-[9px] indent-[22px]'
                  placeholder={t(
                    'eleo-tool-create-image-keywords-placeholder',
                    "What's most important in your image?"
                  )}
                  style={{ fieldSizing: 'content' }}
                  value={state.keywords}
                  onChange={(e) =>
                    dispatch({
                      type: 'SET_KEYWORDS',
                      payload: e.target.value,
                    })
                  }
                />
                <BasicTooltip
                  content={t(
                    'eleo-images-keywords-tooltip-content',
                    "What's most important on the image?"
                  )}
                  title={t('eleo-images-keywords-tooltip-title', 'Keywords')}
                  id='image-keywords-tooltip'
                />

                <div
                  className='text-brand-form-accent absolute left-1 top-1 flex rotate-90 cursor-pointer p-[6px] text-[20px]'
                  data-tooltip-id='image-keywords-tooltip'
                >
                  <VpnKey fontSize='inherit' />
                </div>
              </div>
            </div>
          </div>

          {/* Row 3 */}
          <div className='flex flex-wrap gap-5'>
            {/* Proportions */}
            <div className='min-w-[230px] flex-1 space-y-[5px]'>
              <div className='body-small text-brand-gray-dark pl-[2px]'>
                {t('eleo-tool-create-image-proportions', 'Proportions')}
              </div>

              <div
                className='h-[36px] text-[16px]'
                data-tooltip-id='images-imageCreator-proportions'
              >
                <ReactSelect
                  options={getAvailableProportions()}
                  placeHolder={t('eleo-select-drop-down-placeholder', 'Select')}
                  value={getAvailableProportions()
                    .map((item) => ({
                      ...item,
                      label: (
                        <>
                          <div
                            className='bg-brand-form-accent mr-2 rounded-[2px]'
                            style={{ aspectRatio: item.proportions, height: '14px' }}
                          />
                          {item.label}
                        </>
                      ),
                    }))
                    .find((item) => item.value === state.chosenProportions)}
                  setSelectedValue={(e) => {
                    dispatch({
                      type: 'SET_PROPORTIONS',
                      payload: e.data.value,
                    })
                  }}
                  menuPlacement='top'
                />
              </div>
            </div>

            {/* Amount of images */}
            <div className='min-w-[230px] flex-1 space-y-[5px]'>
              <div className='body-small text-brand-gray-dark pl-[2px]'>
                {t('eleo-tool-create-image-amount', 'Amount of images')}
              </div>

              <div id='samples' className='h-[36px]' data-tooltip-id='images-imageCreator-amount'>
                <ButtonGroup
                  className='w-full'
                  options={[1, 2, 3, 4].map((item) => ({
                    value: item,
                    label: item,
                  }))}
                  value={state.samples}
                  onClick={(event) =>
                    dispatch({
                      type: 'SET_SAMPLES',
                      payload: event.target.value,
                    })
                  }
                />
              </div>
            </div>
          </div>

          {/* Row 4 */}
          <div className='flex flex-wrap gap-5'>
            {/* Quality */}
            <div className='min-w-fit flex-1 space-y-[5px]'>
              <div className='body-small text-brand-gray-dark pl-[2px]'>
                {t('eleo-tool-create-image-speed', 'Speed/Quality')}
              </div>

              <div
                className='h-[36px] min-w-fit text-[16px]'
                data-tooltip-id='images-imageCreator-quality'
              >
                <ButtonGroup
                  className='w-full'
                  options={modes}
                  value={state.quality}
                  onClick={(event) =>
                    dispatch({
                      type: 'SET_QUALITY',
                      payload: event.target.value,
                    })
                  }
                />
              </div>
            </div>

            {/* Creativity */}
            <div className='min-w-[250px] flex-1 space-y-[5px]'>
              <div className='body-small text-brand-gray-dark pl-[2px]'>
                {t('eleo-tool-create-image-creativity', 'Creativity')}
              </div>

              <div className='h-[36px]' data-tooltip-id='images-imageCreator-creativity'>
                <Slider
                  type='advanced'
                  min={0}
                  max={10}
                  step={0.1}
                  showPercentageThreshold={2}
                  value={state.creativity}
                  onChange={(val) =>
                    dispatch({
                      type: 'SET_CREATIVITY',
                      payload: val,
                    })
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <Footer
          handleSubmit={handleSubmit}
          dispatch={dispatch}
          allowStandard={allowStandard}
          isLoading={props.isLoading}
          handleSaveTemplate={handleSaveTemplate}
          handleUpdateTemplate={handleUpdateTemplate}
          handleDeleteTemplate={handleDeleteTemplate}
          lastUsedTemplate={lastUsedTemplate}
          templates={templates}
          isLayoutLarge={props.isLayoutLarge}
          setIsDisplayOutput={props.setIsDisplayOutput}
          history={props.history}
          setTemplate={loadTemplate}
          documentContext={props.documentContext}
          isReady={isReady}
        />
      </div>

      <ContextModal documentContext={props.documentContext} docOptions={['stories']} />
    </>
  )
}

export default ImageForm
